import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Sidebar, CustomText, PrimaryCta, Image, Icon } from '../../../components';
import Utils from '../../../common/utils';
import Core from '../../../common/clientSdk';

// Editable metrics for individual influencer
const INFLUENCER_EDITABLE_METRICS = [
    { key: 'view_count', label: 'Views', field: 'analytics.view_count' },
    { key: 'like_count', label: 'Likes', field: 'analytics.like_count' },
    { key: 'comment_count', label: 'Comments', field: 'analytics.comment_count' },
    { key: 'engagement_rate', label: 'E.R.', field: 'analytics.engagement_rate', readOnly: true, isPercentage: true },
    { key: 'total_engagements', label: 'Total Engagements', field: 'analytics.total_engagements', readOnly: true },
    { key: 'cpe', label: 'CPE', field: 'analytics.cpe', readOnly: true, isCurrency: true },
    { key: 'cpv', label: 'CPV', field: 'analytics.cpv', readOnly: true, isCurrency: true },
    { key: 'impressions', label: 'Impressions', field: 'analytics.impressions' },
    { key: 'reach', label: 'Reach', field: 'analytics.reach' },
    { key: 'share', label: 'Shares', field: 'analytics.share' },
    { key: 'saves', label: 'Saves', field: 'analytics.saves' },
    { key: 'replies', label: 'Replies', field: 'analytics.replies' },
    { key: 'remixes', label: 'Remixes', field: 'analytics.remixes' },
    { key: 'sticker_taps', label: 'Sticker Taps', field: 'analytics.sticker_taps' },
    { key: 'total_clicks', label: 'Total Clicks', field: 'analytics.total_clicks' },
    { key: 'conversions', label: 'Conversions', field: 'analytics.conversions' },
];

const CONTENT_TYPE_MAP = {
    fxt1: "Instagram Image Post",
    ge03: "Instagram Video Post",
    utio: "Instagram Image Story",
    uyts: "Instagram Video Story",
    o5oi: "Instagram Reel",
    ttsn: "Youtube Video",
    vi82: "Youtube Shorts",
};

const EditInfluencerMetricsSidebar = ({
    isOpen,
    onClose,
    influencer,
    campaignId,
    onSaveSuccess,
    onOptimisticUpdate
}) => {
    const [editingMetrics, setEditingMetrics] = useState({});
    const [originalMetrics, setOriginalMetrics] = useState({}); // Track original values
    const [isSaving, setIsSaving] = useState(false);

    // Initialize metrics when sidebar opens or influencer changes
    useEffect(() => {
        if (isOpen && influencer) {
            const currentMetrics = {};
            INFLUENCER_EDITABLE_METRICS.forEach(metric => {
                // Skip populating readOnly metrics from source, we recalculate them
                if (metric.readOnly) return;

                let value = influencer?.analytics?.[metric.key];

                // Fallback for views -> view_count mapping
                if (metric.key === 'view_count' && (value === undefined || value === null || value === '')) {
                    value = influencer?.analytics?.views;
                }

                if (value !== undefined && value !== null && value !== '') {
                    // Format long decimals to max 2 decimal places
                    if (typeof value === 'number') {
                        currentMetrics[metric.key] = Number.isInteger(value) ? value.toString() : value.toFixed(2);
                    } else {
                        currentMetrics[metric.key] = value;
                    }
                } else {
                    currentMetrics[metric.key] = '';
                }
            });
            setEditingMetrics(currentMetrics);
            setOriginalMetrics(currentMetrics); // Store original values
        }
    }, [isOpen, influencer]);

    const handleMetricChange = useCallback((key, value) => {
        setEditingMetrics(prev => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const handleSave = useCallback(() => {
        setIsSaving(true);

        // Only include metrics that have actually changed
        const metricsData = {};
        Object.keys(editingMetrics).forEach(key => {
            const currentValue = editingMetrics[key];
            const originalValue = originalMetrics[key];

            // Compare values (handle empty strings, null, undefined)
            const normalizedCurrent = currentValue === '' || currentValue === null || currentValue === undefined ? '' : String(currentValue);
            const normalizedOriginal = originalValue === '' || originalValue === null || originalValue === undefined ? '' : String(originalValue);

            // Only include if the value has changed
            if (normalizedCurrent !== normalizedOriginal) {
                metricsData[key] = currentValue;
            }
        });

        // If no changes, don't make the API call
        if (Object.keys(metricsData).length === 0) {
            toast.info('No changes to save');
            setIsSaving(false);
            onClose();
            return;
        }

        const params = {
            assignment_id:
                influencer?.originalItem?.assignment_id ||
                influencer?.originalItem?.assignment_data?.assignment_id ||
                influencer?.assignment_id ||
                influencer?.originalItem?.id ||
                influencer?.originalItem?._id ||
                influencer?.originalItem?.sync_id ||
                influencer?.sync_id ||
                influencer?.contentData?.assignment_id, // extensive fallback
            content_id: influencer?.contentData?.id || influencer?.contentKey || influencer?.content_id,
            campaign_id: campaignId || influencer?.originalItem?.campaign_id || influencer?.campaign_id || influencer?.campaign_data?.campaign_id,
            influencer_id: influencer?.influencerData?.influencer_id || influencer?.influencer_id || influencer?.originalItem?.influencer_id,
            matrics_data: metricsData // Only changed fields
        };

        // Optimistic update: Simulate the API payload structure to update UI immediately
        const optimisticResponse = {
            response: [{
                assignment_data: {
                    assignment_id: params.assignment_id
                },
                content_analytics: {
                    [params.content_id]: {
                        ...influencer.analytics,
                        ...metricsData, // use only the changed fields
                        content_type_id: influencer.contentTypeId,
                        content_id: params.content_id
                    }
                }
            }]
        };
        onOptimisticUpdate?.(optimisticResponse);

        const onSuccess = (response) => {
            toast.success(`Metrics saved for ${influencer?.influencerData?.name || 'influencer'}`);
            setIsSaving(false);
            onSaveSuccess?.(response);
            onClose();
        };

        const onError = (error) => {
            console.error("Error updating metrics:", error);
            toast.error(typeof error === 'string' ? error : "Failed to update metrics");
            setIsSaving(false);
        };

        Core.updateContentMetrics(onSuccess, onError, () => {}, params);

    }, [influencer, editingMetrics, originalMetrics, onClose, onSaveSuccess, onOptimisticUpdate, campaignId]);

    const sidebarHeader = () => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <Image
                fallBack={require("../../../resources/images/fallback.png")}
                source={
                    influencer?.influencerData?.profile_picture
                        ? Utils.generateInfluencerDpUrl(
                            influencer?.influencerData?.influencer_id,
                            influencer?.influencerData?.profile_picture
                        )
                        : require("../../../resources/images/fallback.png")
                }
                className="table-profile"
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
            />
            <div>
                <CustomText p text={influencer?.influencerData?.name || 'Edit Metrics'} fontWeight="font_weight_600" />
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                    {CONTENT_TYPE_MAP[influencer?.contentTypeId] || ''}
                </span>
            </div>
        </div>
    );

    const sidebarFooter = () => (
        <div className="manage-section-footer-buttons">
            <PrimaryCta
                text="Cancel"
                onClick={onClose}
                invertSmall
                fullWidth100
                className="manage-section-footer-btn"
            />
            <PrimaryCta
                text="Save Changes"
                onClick={handleSave}
                isLoading={isSaving}
                fullWidth100
                className="manage-section-footer-btn"
            />
        </div>
    );

    // Calculate dynamic metrics based on current editing input
    const getCalculatedMetrics = () => {
        const views = Number(editingMetrics.view_count) || Number(editingMetrics.views) || 0;
        const likes = Number(editingMetrics.like_count) || 0;
        const comments = Number(editingMetrics.comment_count) || 0;
        const shares = Number(editingMetrics.share) || 0;
        const saves = Number(editingMetrics.saves) || 0;

        let reward = Number(influencer?.rewardData?.value?.amount) || 0;
        // Fallback to checking assignment data
        if (!reward) {
             reward = Number(influencer?.assignment_data?.reward_value_client ||
                             influencer?.originalItem?.assignment_data?.reward_value_client) || 0;
        }

        const totalEngagements = likes + comments + shares + saves;

        const engagementRate = views > 0
            ? ((likes + comments + shares + saves) / views) * 100
            : 0;

        const cpe = totalEngagements > 0 ? reward / totalEngagements : 0;
        const cpv = views > 0 ? reward / views : 0;

        return {
            engagement_rate: engagementRate,
            total_engagements: totalEngagements,
            cpe: cpe,
            cpv: cpv
        };
    };

    const calculated = getCalculatedMetrics();

    return (
        <Sidebar
            isOpen={isOpen}
            onClose={onClose}
            Header={sidebarHeader}
            Footer={sidebarFooter}
            sidebarWidth="450px"
            headerAlignment="left"
        >
            <div className="manage-section-sidebar-content">
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="visibility-section">
                        <p className="section-description">
                            Edit the metrics for this influencer's content. Changes will be saved when you click "Save Changes".
                        </p>
                        <div className="toggle-list">
                            {INFLUENCER_EDITABLE_METRICS.map((metric) => {
                                let displayValue = editingMetrics[metric.key] || '';
                                let isDisabled = false;

                                if (metric.readOnly) {
                                    isDisabled = true;
                                    const val = calculated[metric.key] || 0;

                                    if (metric.isPercentage) {
                                        displayValue = `${val.toFixed(2)}%`;
                                    } else if (metric.isCurrency) {
                                        displayValue = val > 0 && val < Infinity
                                            ? `₹${val.toFixed(2)}`
                                            : "₹0";
                                    } else {
                                        displayValue = Utils.changeNumberFormate(Math.round(val));
                                    }
                                }

                                return (
                                    <div key={metric.key} className="toggle-item">
                                        <div className="toggle-item-content">
                                            <span className="toggle-label">{metric.label}</span>
                                        </div>
                                        <div className="metric-input-with-tooltip">
                                            <input
                                                type="text"
                                                className={`customize-metric-input ${isDisabled ? 'disabled-input' : ''}`}
                                                value={displayValue}
                                                placeholder={isDisabled ? '-' : `Enter ${metric.label}`}
                                                disabled={isDisabled}
                                                readOnly={isDisabled}
                                                style={isDisabled ? { backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' } : {}}
                                                onChange={(e) => !isDisabled && handleMetricChange(metric.key, e.target.value)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </Sidebar>
    );
};

export default EditInfluencerMetricsSidebar;
